import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  X,
  RefreshCw,
  FileText,
  Package,
  Truck
} from 'lucide-react';
import { rateExclToIncl } from './POItemsTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function POBulkUploadTab({ onItemsImported, disabled }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  // Download sample template
  const downloadTemplate = () => {
    // NOTE: purchase_rate column value must be INCLUSIVE of GST per company policy.
    const csvContent = `supplier_name,product_sku,barcode,quantity,unit,purchase_rate_incl_gst,gst_percentage
ABC Suppliers,PROD-000001,,100,PCS,,
ABC Suppliers,PROD-000002,,50,BOX,177.00,18
XYZ Trading,PROD-000003,1234567890,25,CASE,,12`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'po_bulk_upload_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded');
  };

  // Robustly split a CSV line respecting double-quoted values (handles commas in quoted cells)
  const splitCSVLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        out.push(cur); cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(v => v.replace(/\r$/, '').trim());
  };

  // Parse CSV file
  const parseCSV = useCallback((text) => {
    // Normalise line endings and strip BOM
    const lines = text.replace(/^\uFEFF/, '').trim().split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length < 2) {
      toast.error('CSV file must have a header row and at least one data row');
      return [];
    }

    const headers = splitCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = splitCSVLine(lines[i]);
      // Accept rows even if trailing empty cells are missing — pad with empty strings
      while (values.length < headers.length) values.push('');
      const row = { row_number: i };
      headers.forEach((header, idx) => {
        row[header] = values[idx] !== undefined ? values[idx] : '';
      });
      // Convert numeric fields; empty strings MUST become null (Pydantic rejects "" for Optional[float])
      const q = row.quantity;
      row.quantity = (q === '' || q === undefined || q === null) ? 0 : (parseFloat(q) || 0);
      const pr = row.purchase_rate_incl_gst !== undefined ? row.purchase_rate_incl_gst : row.purchase_rate;
      row.purchase_rate = (pr === '' || pr === undefined || pr === null) ? null : (parseFloat(pr) || null);
      const gp = row.gst_percentage;
      if (gp === '' || gp === undefined || gp === null) {
        row.gst_percentage = null;
      } else {
        const parsed = parseFloat(gp);
        row.gst_percentage = isNaN(parsed) ? null : parsed;
      }
      // Normalize empty string barcode/sku/supplier to empty string (backend Optional[str] accepts empty)
      data.push(row);
    }

    return data;
    // splitCSVLine is stable within this closure; no external deps needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle file selection
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const isCSV = selectedFile.name.endsWith('.csv');
    
    if (!isCSV && !validTypes.includes(selectedFile.type)) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);
    setParsing(true);
    setValidationResult(null);

    try {
      const text = await selectedFile.text();
      const parsedData = parseCSV(text);
      
      if (parsedData.length === 0) {
        toast.error('No valid data found in file');
        setFile(null);
        return;
      }

      setRawData(parsedData);
      toast.success(`Parsed ${parsedData.length} rows from file`);
    } catch (err) {
      toast.error('Failed to parse file');
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  // Convert validated items into the PO-form shape
  // Extracted so we can reuse it from both the explicit "Import" button
  // and the auto-import that fires once validation succeeds.
  //
  // GST convention: the CSV `purchase_rate_incl_gst` column is INCLUSIVE of GST
  // (business policy — same as the manual PO flow). The backend `/bulk-validate`
  // endpoint returns `purchase_rate` as EXCL-GST (already divided by 1+gst%),
  // so we convert it back to INCL-GST here to match the parent form's state
  // convention. The parent form (POTabDialog / POFormDialog) will run its own
  // INCL→EXCL conversion once when submitting to the backend. Skipping this
  // step causes a double-conversion bug: 105 → 100 (validate) → 95.24 (submit).
  const buildFormItemsFromValidation = (vr) => {
    const valid = (vr?.validated_items || []).filter(it => it.is_valid);
    return valid.map(item => {
      const qty = Number(item.quantity) || 0;
      const rateExcl = Number(item.purchase_rate) || 0;
      const gst = Number(item.gst_percentage) || 0;
      const rateIncl = rateExclToIncl(rateExcl, gst);
      const amountBeforeTax = qty * rateExcl;
      const gstAmount = amountBeforeTax * gst / 100;
      return {
        _uid: `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        product_id: item.product_id,
        product_name: item.product_name,
        product_sku: item.product_sku,
        quantity: qty,
        unit: item.unit,
        purchase_rate: rateIncl,
        discount_percentage: 0,
        gst_percentage: gst,
        amount_before_tax: amountBeforeTax,
        gst_amount: gstAmount,
        line_total: amountBeforeTax + gstAmount,
      };
    });
  };

  // Validate data with backend
  const validateData = async () => {
    if (rawData.length === 0) {
      toast.error('No data to validate');
      return;
    }

    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/purchase-orders/bulk-validate`,
        rawData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setValidationResult(response.data);

      // Auto-import valid rows into the parent PO form the moment validation
      // succeeds — without this the user has to hit a separate "Import N valid
      // items" button and, if they skip it, the footer "Create PO" button
      // complains with "Please add at least one item".
      const autoItems = buildFormItemsFromValidation(response.data);
      if (autoItems.length > 0) {
        onItemsImported(autoItems, response.data.supplier_groups);
      }

      if (response.data.success) {
        toast.success(`All ${response.data.valid_rows} rows validated & imported. You can now Create the PO.`);
      } else if (autoItems.length > 0) {
        toast.warning(`${response.data.error_rows} rows have errors. Imported ${autoItems.length} valid ${autoItems.length === 1 ? 'row' : 'rows'}; fix errors & re-upload for the rest.`);
      } else {
        toast.warning(`${response.data.error_rows} rows have errors. Please fix and re-upload.`);
      }
    } catch (err) {
      // Safely extract error detail — Pydantic 422 returns an ARRAY of error objects
      // which React/Sonner cannot render as children → causes "blank screen" crash.
      const detail = err?.response?.data?.detail;
      let msg = 'Validation failed';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.slice(0, 3).map(d => {
          const loc = Array.isArray(d?.loc) ? d.loc.join('.') : '';
          return `${loc ? loc + ': ' : ''}${d?.msg || JSON.stringify(d)}`;
        }).join(' | ');
        if (detail.length > 3) msg += ` (+${detail.length - 3} more)`;
      } else if (detail && typeof detail === 'object') {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setValidating(false);
    }
  };

  // Manual re-import button (kept as a safety net — users can still trigger it
  // explicitly if they have navigated away and want to re-populate the form).
  const importToForm = () => {
    if (!validationResult) {
      toast.error('Please validate data first');
      return;
    }
    const items = buildFormItemsFromValidation(validationResult);
    if (items.length === 0) {
      toast.error('No valid rows to import. Please fix the errors and re-upload.');
      return;
    }
    onItemsImported(items, validationResult.supplier_groups);
    toast.success(`Imported ${items.length} valid items${validationResult.error_rows ? ` (${validationResult.error_rows} errors skipped)` : ''}`);
  };

  // Download error report as CSV
  const downloadErrorReport = () => {
    const errs = (validationResult?.validated_items || []).filter(i => !i.is_valid);
    if (errs.length === 0) { toast.info('No errors to export'); return; }
    const header = 'row_number,error\n';
    const rows = errs.map(e => `${e.row_number},"${(e.error || '').replace(/"/g, '""')}"`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `po_bulk_errors_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Error report downloaded (${errs.length} rows)`);
  };

  // Clear everything
  const clearUpload = () => {
    setFile(null);
    setRawData([]);
    setValidationResult(null);
    setShowErrors(false);
  };

  const errorItems = validationResult?.validated_items?.filter(i => !i.is_valid) || [];
  const validItems = validationResult?.validated_items?.filter(i => i.is_valid) || [];

  return (
    <div className="space-y-6" data-testid="bulk-upload-tab">
      {/* Instructions */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-800">Bulk Upload Instructions</h3>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>• Download the CSV template and fill in your order details</li>
              <li>• Supplier Name and Product SKU must match your Master Data</li>
              <li>• Quantity is required and must be greater than 0</li>
              <li>• <strong>Purchase Rate is INCLUSIVE of GST</strong> (backend converts to excl-GST internally). Leave blank to use catalogue B2B price.</li>
              <li>• System will auto-create separate POs for each supplier</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Download Template */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="gap-2"
          data-testid="download-template-btn"
        >
          <Download size={16} />
          Download Template
        </Button>
        <span className="text-sm text-gray-500">CSV format with sample data</span>
      </div>

      {/* File Upload */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Upload CSV File</Label>
        <div className="flex items-center gap-3">
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={disabled || parsing}
            className="flex-1"
            data-testid="file-input"
          />
          {file && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearUpload}
              className="text-red-500"
            >
              <X size={16} />
            </Button>
          )}
        </div>
        {file && (
          <p className="text-sm text-gray-600">
            <FileText className="inline w-4 h-4 mr-1" />
            {file.name} ({rawData.length} rows)
          </p>
        )}
      </div>

      {/* Raw Data Preview */}
      {rawData.length > 0 && !validationResult && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 font-medium text-sm flex items-center justify-between">
            <span>Preview ({rawData.length} rows)</span>
            <Button
              size="sm"
              onClick={validateData}
              disabled={validating}
              className="gap-2"
              data-testid="validate-btn"
            >
              {validating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Validate Data
            </Button>
          </div>
          <div className="max-h-60 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Supplier</th>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-center">Qty</th>
                  <th className="px-3 py-2 text-center">Unit</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-center">GST%</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rawData.slice(0, 10).map((row, idx) => (
                  <tr key={`raw-${row.row_number || idx}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{row.row_number}</td>
                    <td className="px-3 py-2">{row.supplier_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.product_sku}</td>
                    <td className="px-3 py-2 text-center">{row.quantity}</td>
                    <td className="px-3 py-2 text-center">{row.unit || 'PCS'}</td>
                    <td className="px-3 py-2 text-right">{row.purchase_rate || '-'}</td>
                    <td className="px-3 py-2 text-center">{row.gst_percentage || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rawData.length > 10 && (
              <p className="text-center text-sm text-gray-500 py-2">
                + {rawData.length - 10} more rows
              </p>
            )}
          </div>
        </div>
      )}

      {/* Validation Results */}
      {validationResult && (() => {
        // Summary panel classes + heading label (flattened from nested ternaries).
        let summaryBoxClass;
        let summaryHeading;
        let summaryHeadingClass;
        if (validationResult.success) {
          summaryBoxClass = 'bg-green-50 border-green-200';
          summaryHeading = 'Validation Successful!';
          summaryHeadingClass = 'text-green-800';
        } else if (validItems.length > 0) {
          summaryBoxClass = 'bg-amber-50 border-amber-200';
          summaryHeading = 'Partial Validation — valid rows can still be imported';
          summaryHeadingClass = 'text-amber-800';
        } else {
          summaryBoxClass = 'bg-red-50 border-red-200';
          summaryHeading = 'Validation Failed — no valid rows';
          summaryHeadingClass = 'text-amber-800';
        }
        return (
        <div className="space-y-4">
          {/* Summary */}
          <div className={`p-4 rounded-lg border ${summaryBoxClass}`}>
            <div className="flex items-center gap-3">
              {validationResult.success ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              )}
              <div className="flex-1">
                <h3 className={`font-semibold ${summaryHeadingClass}`}>
                  {summaryHeading}
                </h3>
                <p className="text-sm mt-1">
                  <span className="text-green-600 font-medium">{validationResult.valid_rows} valid</span>
                  {validationResult.error_rows > 0 && (
                    <span className="text-red-600 font-medium ml-3">{validationResult.error_rows} errors</span>
                  )}
                  <span className="text-gray-500 ml-3">/ {validationResult.total_rows} total</span>
                </p>
              </div>
              {validItems.length > 0 && (
                <Button onClick={importToForm} className="gap-2" data-testid="import-btn">
                  <Package size={16} />
                  Import {validItems.length} valid {validItems.length === 1 ? 'item' : 'items'}
                </Button>
              )}
            </div>
          </div>

          {/* Error List */}
          {errorItems.length > 0 && (
            <div className="border border-red-200 rounded-lg overflow-hidden">
              <div className="w-full bg-red-50 px-4 py-3 flex items-center justify-between">
                <button
                  className="flex items-center gap-2 text-left"
                  onClick={() => setShowErrors(!showErrors)}
                >
                  <span className="font-medium text-red-800 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    {errorItems.length} Validation Errors
                  </span>
                  <span className="text-sm text-red-600 ml-2">{showErrors ? 'Hide' : 'Show'}</span>
                </button>
                <Button variant="outline" size="sm" onClick={downloadErrorReport} className="gap-1 text-red-700 border-red-300 hover:bg-red-100" data-testid="download-errors-btn">
                  <Download size={14} />
                  Download Error Report
                </Button>
              </div>
              {showErrors && (
                <div className="max-h-48 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Row</th>
                        <th className="px-3 py-2 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {errorItems.map((item, idx) => (
                        <tr key={`err-${item.row_number || idx}`} className="bg-red-50">
                          <td className="px-3 py-2 font-mono">{item.row_number}</td>
                          <td className="px-3 py-2 text-red-700">{item.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Supplier Groups Preview */}
          {Object.keys(validationResult.supplier_groups || {}).length > 0 && (
            <div className="border rounded-lg">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <Truck size={16} />
                  POs to be Created ({Object.keys(validationResult.supplier_groups).length} suppliers)
                </h4>
                <p className="text-sm text-blue-600 mt-1">
                  Each supplier will receive a separate Purchase Order
                </p>
              </div>
              <div className="divide-y max-h-72 overflow-auto">
                {Object.values(validationResult.supplier_groups).map((group, idx) => (
                  <div key={group.supplier_name || `grp-${idx}`} className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{group.supplier_name}</span>
                      <Badge variant="outline">{group.items.length} items</Badge>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      {group.items.slice(0, 3).map((item, i) => (
                        <div key={item.product_sku || `itm-${idx}-${i}`} className="flex justify-between">
                          <span>{item.product_name}</span>
                          <span className="font-mono">{item.quantity} {item.unit}</span>
                        </div>
                      ))}
                      {group.items.length > 3 && (
                        <p className="text-gray-400">+ {group.items.length - 3} more items</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={clearUpload}>
              Clear & Start Over
            </Button>
            {!validationResult.success && (
              <Button variant="outline" onClick={downloadTemplate}>
                Download Template Again
              </Button>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default POBulkUploadTab;
